import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import {
  addDiagnosis, addPrescription,
  getConsultation, listVaccinations, removeDiagnosis, removePrescription,
  updateConsultation, upsertSoap, upsertVitals,
} from '../../api/clinical'
import { getPatient } from '../../api/patients'
import { listBatches, listMovements, listProducts } from '../../api/stock'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { useToast } from '../../components/ui/Toast'
import { useAuth } from '../../context/AuthContext'
import { useFetch } from '../../hooks/useFetch'
import { crumbLinkClass, tdClass, thClass } from '../../lib/tableStyles'
import type { ConsultationDetail as ConsultationDetailType, Diagnosis, Prescription, Vaccination } from '../../types/clinical'
import type { ProductListItem, StockBatchListItem, StockMovementRead } from '../../types/stock'
import AppointmentBookingModal from '../appointments/AppointmentBookingModal'
import { DispenseForm } from '../stock/DispenseForm'

const soapLabelClass =
  'text-xs font-bold text-text-2 uppercase tracking-[0.05em] mb-[0.35rem] block'

const inlineInputClass =
  'py-[0.45rem] px-[0.65rem] border border-border-input rounded-md text-[0.875rem] outline-none box-border bg-surface text-text w-full'

const textareaInlineClass =
  'w-full py-[0.65rem] px-[0.75rem] border border-border-input rounded-md text-[0.875rem] outline-none box-border resize-y min-h-[90px] font-[inherit] bg-surface text-text'

// ── SOAP Section ──────────────────────────────────────────────────────────────

function SoapSection({ consultationId, initial }: { consultationId: number; initial: ConsultationDetailType['soap_note'] }) {
  const toast = useToast()
  const [fields, setFields] = useState({
    subjective: initial?.subjective ?? '',
    objective: initial?.objective ?? '',
    assessment: initial?.assessment ?? '',
    plan: initial?.plan ?? '',
  })
  const [saving, setSaving] = useState(false)

  const handleBlur = async (field: keyof typeof fields) => {
    setSaving(true)
    try {
      await upsertSoap(consultationId, { [field]: fields[field] || null })
    } catch {
      toast.error('Failed to save SOAP note.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>SOAP Note</SectionTitle>
        {saving && <span className="text-xs text-text-3">Saving…</span>}
      </div>
      <div className="grid gap-4">
        {(['subjective', 'objective', 'assessment', 'plan'] as const).map((field) => (
          <div key={field}>
            <span className={soapLabelClass}>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
            <textarea
              className={textareaInlineClass}
              value={fields[field]}
              onChange={(e) => setFields(f => ({ ...f, [field]: e.target.value }))}
              onBlur={() => void handleBlur(field)}
              placeholder={
                field === 'subjective' ? 'Owner-reported history and symptoms…' :
                field === 'objective' ? 'Physical examination findings…' :
                field === 'assessment' ? 'Clinical impression and diagnoses…' :
                'Treatment plan, medications, follow-up…'
              }
            />
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── Vitals Section ────────────────────────────────────────────────────────────

function VitalsSection({ consultationId, initial }: { consultationId: number; initial: ConsultationDetailType['vital_signs'] }) {
  const toast = useToast()
  const [weight, setWeight] = useState(initial?.weight_kg ?? '')
  const [temp, setTemp] = useState(initial?.temperature_c ?? '')
  const [hr, setHr] = useState(initial?.heart_rate != null ? String(initial.heart_rate) : '')
  const [rr, setRr] = useState(initial?.respiratory_rate != null ? String(initial.respiratory_rate) : '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [saving, setSaving] = useState(false)

  const saveWithValues = async (vals: { weight: string; temp: string; hr: string; rr: string; notes: string }) => {
    setSaving(true)
    try {
      await upsertVitals(consultationId, {
        weight_kg: vals.weight !== '' ? vals.weight : null,
        temperature_c: vals.temp !== '' ? vals.temp : null,
        heart_rate: vals.hr !== '' ? Number(vals.hr) : null,
        respiratory_rate: vals.rr !== '' ? Number(vals.rr) : null,
        notes: String(vals.notes).trim() || null,
      })
    } catch {
      toast.error('Failed to save vitals.')
    } finally {
      setSaving(false)
    }
  }

  const handleBlur = () => {
    void saveWithValues({ weight, temp, hr, rr, notes })
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <SectionTitle style={{ margin: 0 }}>Vital Signs</SectionTitle>
        {saving && <span className="text-[0.78rem] text-text-3">Saving…</span>}
      </div>
      <div className="grid grid-cols-2 gap-x-5 gap-y-3 mb-3 max-sm:grid-cols-1">
        <div>
          <label className={soapLabelClass}>Weight (kg)</label>
          <input className={inlineInputClass} type="number" step="0.01" min="0" value={String(weight)} onChange={e => setWeight(e.target.value as unknown as string)} onBlur={handleBlur} placeholder="e.g. 4.5" />
        </div>
        <div>
          <label className={soapLabelClass}>Temperature (°C)</label>
          <input className={inlineInputClass} type="number" step="0.1" value={String(temp)} onChange={e => setTemp(e.target.value as unknown as string)} onBlur={handleBlur} placeholder="e.g. 38.5" />
        </div>
        <div>
          <label className={soapLabelClass}>Heart Rate (bpm)</label>
          <input className={inlineInputClass} type="number" min="0" value={hr} onChange={e => setHr(e.target.value)} onBlur={handleBlur} placeholder="e.g. 80" />
        </div>
        <div>
          <label className={soapLabelClass}>Respiratory Rate (br/min)</label>
          <input className={inlineInputClass} type="number" min="0" value={rr} onChange={e => setRr(e.target.value)} onBlur={handleBlur} placeholder="e.g. 20" />
        </div>
      </div>
      <div className="mb-3">
        <label className={soapLabelClass}>Notes</label>
        <input className={inlineInputClass} type="text" value={notes} onChange={e => setNotes(e.target.value)} onBlur={handleBlur} placeholder="e.g. Uncooperative patient" />
      </div>
    </Card>
  )
}

// ── Diagnoses Section ─────────────────────────────────────────────────────────

function DiagnosesSection({ consultationId, initial }: { consultationId: number; initial: Diagnosis[] }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [items, setItems] = useState<Diagnosis[]>(initial)
  const [newDesc, setNewDesc] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [adding, setAdding] = useState(false)

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault()
    if (!newDesc.trim() || adding) return
    setAdding(true)
    try {
      const d = await addDiagnosis(consultationId, { description: newDesc.trim(), is_primary: isPrimary })
      setItems(prev => [...prev, d])
      setNewDesc('')
      setIsPrimary(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add diagnosis.')
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (d: Diagnosis) => {
    const ok = await confirm({ title: 'Remove diagnosis?', message: d.description, confirmLabel: 'Remove' })
    if (!ok) return
    try {
      await removeDiagnosis(d.id)
      setItems(prev => prev.filter(x => x.id !== d.id))
    } catch {
      toast.error('Failed to remove diagnosis.')
    }
  }

  return (
    <Card>
      <SectionTitle>Diagnoses</SectionTitle>
      {items.length === 0 ? (
        <p className="text-[0.875rem] text-text-3 mt-0">No diagnoses added yet.</p>
      ) : (
        <ul className="list-none p-0 m-0 mb-4">
          {items.map(d => (
            <li key={d.id} className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-[0.9rem] text-text">
                {d.is_primary && (
                  <span className="bg-[var(--accent-muted)] text-accent rounded-full text-[0.7rem] font-bold py-[0.1rem] px-[0.45rem] mr-2">
                    Primary
                  </span>
                )}
                {d.description}
              </span>
              <button
                className="bg-transparent border-none cursor-pointer text-danger-text text-[0.8rem] px-[0.25rem]"
                onClick={() => void handleRemove(d)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={(e) => void handleAdd(e)} className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className={soapLabelClass}>Add diagnosis</label>
          <input className={inlineInputClass} type="text" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Diagnosis description…" />
        </div>
        <label className="flex items-center gap-[0.35rem] text-[0.85rem] cursor-pointer mb-[1px]">
          <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} />
          Primary
        </label>
        <Button type="submit" size="sm" disabled={adding || !newDesc.trim()}>{adding ? '…' : 'Add'}</Button>
      </form>
    </Card>
  )
}

// ── Prescriptions Section ─────────────────────────────────────────────────────

function PrescriptionsSection({
  consultationId,
  practiceId,
  initial,
  onDispensed,
}: {
  consultationId: number
  practiceId: number
  initial: Prescription[]
  onDispensed?: () => void
}) {
  const toast = useToast()
  const confirm = useConfirm()
  const [items, setItems] = useState<Prescription[]>(initial)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' })
  const [adding, setAdding] = useState(false)

  // Product search state
  const [productQuery, setProductQuery] = useState('')
  const [productSuggestions, setProductSuggestions] = useState<ProductListItem[]>([])
  const [selectedProduct, setSelectedProduct] = useState<ProductListItem | null>(null)
  const [availableBatches, setAvailableBatches] = useState<StockBatchListItem[]>([])
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [dispenseNow, setDispenseNow] = useState(true)
  const [dispenseQty, setDispenseQty] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }))

  // Debounced product search
  useEffect(() => {
    if (!productQuery.trim() || selectedProduct) {
      setProductSuggestions([])
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await listProducts({ practice_id: practiceId, search: productQuery.trim(), is_active: true })
        setProductSuggestions(results.slice(0, 8))
      } catch {
        // non-critical — ignore
      }
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [productQuery, practiceId, selectedProduct])

  // Fetch batches when product selected
  useEffect(() => {
    if (!selectedProduct) {
      setAvailableBatches([])
      setDispenseQty('')
      return
    }
    setLoadingBatches(true)
    listBatches({ practice_id: practiceId, product_id: selectedProduct.id })
      .then(batches => {
        const available = batches.filter(b => !b.is_expired && parseFloat(b.quantity_on_hand) > 0)
        setAvailableBatches(available)
        setDispenseQty('')
      })
      .catch(() => setAvailableBatches([]))
      .finally(() => setLoadingBatches(false))
  }, [selectedProduct, practiceId])

  const totalStock = availableBatches.reduce((sum, b) => sum + parseFloat(b.quantity_on_hand), 0)
  // FIFO: earliest expiry first, then earliest received
  const fifoBatch = availableBatches.sort((a, b) => {
    if (!a.expiry_date && !b.expiry_date) return a.id - b.id
    if (!a.expiry_date) return 1
    if (!b.expiry_date) return -1
    return a.expiry_date.localeCompare(b.expiry_date)
  })[0] ?? null

  const handleSelectProduct = (p: ProductListItem) => {
    setSelectedProduct(p)
    setProductQuery(p.name)
    setProductSuggestions([])
    if (!form.medication_name.trim()) {
      setForm(f => ({ ...f, medication_name: p.name }))
    }
  }

  const handleClearProduct = () => {
    setSelectedProduct(null)
    setProductQuery('')
    setAvailableBatches([])
    setDispenseQty('')
    setDispenseNow(true)
  }

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.medication_name.trim() || adding) return
    const dispensing = selectedProduct && fifoBatch && dispenseNow && dispenseQty && parseFloat(dispenseQty) > 0
    setAdding(true)
    try {
      const p = await addPrescription(consultationId, {
        medication_name: form.medication_name.trim(),
        dosage: form.dosage.trim() || null,
        frequency: form.frequency.trim() || null,
        duration: form.duration.trim() || null,
        instructions: form.instructions.trim() || null,
        product_id: dispensing ? selectedProduct.id : null,
        stock_batch_id: dispensing ? fifoBatch.id : null,
        quantity_dispensed: dispensing ? dispenseQty : null,
      })
      setItems(prev => [...prev, p])
      setForm({ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' })
      handleClearProduct()
      setShowForm(false)
      if (dispensing) onDispensed?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add prescription.')
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (p: Prescription) => {
    const ok = await confirm({ title: 'Remove prescription?', message: p.medication_name, confirmLabel: 'Remove' })
    if (!ok) return
    try {
      await removePrescription(p.id)
      setItems(prev => prev.filter(x => x.id !== p.id))
    } catch {
      toast.error('Failed to remove prescription.')
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setForm({ medication_name: '', dosage: '', frequency: '', duration: '', instructions: '' })
    handleClearProduct()
  }

  return (
    <Card>
      <SectionTitle>Prescriptions</SectionTitle>
      {items.length === 0 ? (
        <p className="text-[0.875rem] text-text-3 mt-0">No prescriptions added yet.</p>
      ) : (
        <div className="mb-4">
          {items.map(p => (
            <div key={p.id} className="py-[0.65rem] border-b border-border">
              <div className="flex justify-between items-start">
                <div>
                  <span className="font-semibold text-[0.9rem] text-text">{p.medication_name}</span>
                  {(p.dosage || p.frequency || p.duration) && (
                    <span className="text-[0.82rem] text-text-2 ml-2">
                      {[p.dosage, p.frequency, p.duration].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {p.instructions && <div className="text-[0.8rem] text-text-3 mt-[0.2rem]">{p.instructions}</div>}
                  {p.quantity_dispensed && (
                    <div className="text-[0.78rem] text-success-text mt-[0.15rem] font-medium">
                      Dispensed: {p.quantity_dispensed}
                    </div>
                  )}
                </div>
                <button
                  className="bg-transparent border-none cursor-pointer text-danger-text text-[0.8rem] px-[0.25rem] shrink-0"
                  onClick={() => void handleRemove(p)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <form onSubmit={(e) => void handleAdd(e)} className="flex flex-col gap-3">
          {/* Product picker */}
          <div className="relative">
            <label className={soapLabelClass}>Product (optional — links to stock)</label>
            <div className="flex gap-2 items-center">
              <input
                className={inlineInputClass}
                type="text"
                value={productQuery}
                onChange={e => { setProductQuery(e.target.value); if (selectedProduct) handleClearProduct() }}
                placeholder="Search product catalogue…"
                autoComplete="off"
              />
              {selectedProduct && (
                <button
                  type="button"
                  onClick={handleClearProduct}
                  className="text-[0.8rem] text-text-3 whitespace-nowrap hover:text-danger-text transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            {productSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-surface border border-border rounded-md shadow-lg z-50 overflow-hidden">
                {productSuggestions.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseDown={() => handleSelectProduct(p)}
                    className="w-full px-3 py-2 text-left text-[0.85rem] hover:bg-surface-alt transition-colors"
                  >
                    <span className="font-medium">{p.name}</span>
                    {p.generic_name && <span className="text-text-3 ml-2 text-[0.8rem]">{p.generic_name}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Stock status */}
          {selectedProduct && !loadingBatches && (
            totalStock > 0 ? (
              <div className="rounded-md border border-success-border bg-success-bg px-3 py-2 text-[0.82rem] text-success-text">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span>
                    <strong>{totalStock} {fifoBatch?.product_unit ?? 'units'}</strong> in stock
                    {fifoBatch?.expiry_date && <span className="ml-1 opacity-70">(batch exp. {fifoBatch.expiry_date})</span>}
                  </span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dispenseNow}
                      onChange={e => setDispenseNow(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="font-semibold">Dispense now</span>
                  </label>
                </div>
                {dispenseNow && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-[0.8rem] font-semibold shrink-0">Quantity to dispense</label>
                    <NumberStepper
                      className="max-w-[150px]"
                      min={0.001}
                      max={totalStock}
                      step={0.001}
                      value={dispenseQty}
                      onChange={setDispenseQty}
                      placeholder="e.g. 14"
                    />
                    <span className="text-[0.8rem] text-text-3">{fifoBatch?.product_unit}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-[0.82rem] text-warning-text">
                No stock available — prescription will be written for the client to fill at a pharmacy.
              </div>
            )
          )}
          {selectedProduct && loadingBatches && (
            <div className="text-[0.82rem] text-text-3">Checking stock…</div>
          )}

          {/* Rx fields */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-sm:grid-cols-1">
            <div className="col-span-2 max-sm:col-span-1">
              <label className={soapLabelClass}>
                Medication name <span className="text-danger-text">*</span>
              </label>
              <input className={inlineInputClass} type="text" value={form.medication_name} onChange={e => set('medication_name', e.target.value)} placeholder="e.g. Amoxicillin 250mg" required />
            </div>
            <div>
              <label className={soapLabelClass}>Dosage</label>
              <input className={inlineInputClass} type="text" value={form.dosage} onChange={e => set('dosage', e.target.value)} placeholder="e.g. 10mg/kg" />
            </div>
            <div>
              <label className={soapLabelClass}>Frequency</label>
              <input className={inlineInputClass} type="text" value={form.frequency} onChange={e => set('frequency', e.target.value)} placeholder="e.g. Twice daily" />
            </div>
            <div>
              <label className={soapLabelClass}>Duration</label>
              <input className={inlineInputClass} type="text" value={form.duration} onChange={e => set('duration', e.target.value)} placeholder="e.g. 7 days" />
            </div>
            <div>
              <label className={soapLabelClass}>Instructions</label>
              <input className={inlineInputClass} type="text" value={form.instructions} onChange={e => set('instructions', e.target.value)} placeholder="e.g. Give with food" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={adding || !form.medication_name.trim()}>
              {adding ? '…' : (selectedProduct && dispenseNow && dispenseQty && parseFloat(dispenseQty) > 0)
                ? 'Prescribe & Dispense'
                : 'Add Prescription'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={handleCancel}>Cancel</Button>
          </div>
        </form>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>+ Add Prescription</Button>
      )}
    </Card>
  )
}

// ── Vaccination Status Panel ──────────────────────────────────────────────────

function vaccinationStatus(nextDue: string | null): { label: string; color: string; bg: string } {
  if (!nextDue) return { label: 'Recorded', color: 'var(--text-3)', bg: 'var(--surface-alt)' }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(nextDue)
  const daysUntil = Math.round((due.getTime() - today.getTime()) / 86_400_000)
  if (daysUntil < 0)  return { label: 'Overdue',    color: 'var(--danger-text)', bg: 'var(--danger-bg)' }
  if (daysUntil <= 30) return { label: 'Due soon',   color: 'var(--warning-text)', bg: 'var(--warning-bg)' }
  return { label: 'Up to date', color: 'var(--success-text)', bg: 'var(--success-bg)' }
}

function VaccinationPanel({ patientId }: { patientId: number }) {
  const { data: vaccinations, loading } = useFetch<Vaccination[]>(
    () => listVaccinations(patientId),
    [patientId],
  )

  if (loading) return null

  return (
    <Card>
      <SectionTitle>Vaccination Status</SectionTitle>
      {!vaccinations || vaccinations.length === 0 ? (
        <p className="m-0 text-[0.875rem] text-text-3 italic">No vaccinations on record.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {vaccinations.map(v => {
            const { label, color, bg } = vaccinationStatus(v.next_due_date)
            return (
              <div key={v.id} className="flex items-center justify-between py-[0.45rem] px-3 rounded-md border border-border bg-surface-alt">
                <div>
                  <span className="font-semibold text-[0.88rem] text-text">{v.vaccine_name}</span>
                  <span className="text-text-3 text-xs ml-2">
                    Last: {new Date(v.date_given).toLocaleDateString()}
                    {v.next_due_date && ` · Due: ${new Date(v.next_due_date).toLocaleDateString()}`}
                  </span>
                </div>
                <span className="text-xs font-semibold px-2 py-[0.2rem] rounded-full shrink-0" style={{ color, background: bg }}>
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// ── Main ConsultationDetail ───────────────────────────────────────────────────

export default function ConsultationDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [showFollowUp, setShowFollowUp] = useState(false)

  // Org admins are business/practice management, not clinical staff — the backend
  // already rejects them from writing consultations, SOAP notes, vitals, diagnoses,
  // prescriptions, and dispensing, so the UI shows the same read-only view it uses
  // for completed (locked) consultations rather than a form that would just 403.
  const canEditClinical = user?.role !== 'org_admin'

  const [movementsKey, setMovementsKey] = useState(0)

  const { data: consultation, loading, error, refetch } = useFetch(
    () => getConsultation(Number(id)),
    [id],
  )

  const { data: patient } = useFetch(
    () => consultation?.patient_id ? getPatient(consultation.patient_id) : Promise.resolve(null),
    [consultation?.patient_id],
  )

  function followUpDate() { return new Date().toISOString().slice(0, 10) }
  function followUpTime() {
    const d = new Date()
    d.setHours(d.getHours() + 1, 0, 0, 0)
    return `${String(d.getHours()).padStart(2, '0')}:00`
  }

  // The client to lock the follow-up booking to: primary active owner, falling back
  // to any active owner, then any owner at all — patients should always have one.
  const followUpClientId = patient?.owners.find(o => o.is_primary && o.status === 'active')?.client_id
    ?? patient?.owners.find(o => o.status === 'active')?.client_id
    ?? patient?.owners[0]?.client_id

  const handleComplete = useCallback(async () => {
    if (!consultation) return
    const ok = await confirm({
      title: 'Mark as completed?',
      message: 'Once completed, the consultation will be locked for editing.',
      confirmLabel: 'Complete',
    })
    if (!ok) return
    try {
      await updateConsultation(consultation.id, { status: 'completed' })
      toast.success('Consultation marked as completed.')
      refetch()
    } catch {
      toast.error('Failed to update status.')
    }
  }, [consultation, confirm, toast, refetch])

  const handleReopen = useCallback(async () => {
    if (!consultation) return
    try {
      await updateConsultation(consultation.id, { status: 'open' })
      toast.success('Consultation reopened.')
      refetch()
    } catch {
      toast.error('Failed to reopen.')
    }
  }, [consultation, toast, refetch])

  if (loading) return <div className="text-text-2 py-8">Loading…</div>
  if (error || !consultation) return <ErrorBox>{error ?? 'Consultation not found.'}</ErrorBox>

  const isCompleted = consultation.status === 'completed'

  return (
    <div className="max-w-[860px]">
      {patient?.allergies && (
        <div className="bg-danger-bg border border-danger-border rounded-md px-4 py-3 mb-5 flex items-start gap-[0.6rem] text-sm">
          <AlertTriangle size={16} strokeWidth={1.75} className="text-danger-text shrink-0 mt-[1px]" />
          <div>
            <span className="font-bold text-danger-text text-[0.875rem]">Allergies: </span>
            <span className="text-danger-text text-[0.875rem]">{patient.allergies}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <nav className="text-[0.82rem] text-text-3 mb-2 flex gap-[0.4rem] items-center">
            <Link to={`/patients/${consultation.patient_id}`} className={crumbLinkClass}>← Patient</Link>
          </nav>
          <h1 className="m-0 text-[1.3rem] font-bold text-text-heading">
            Consultation — {new Date(consultation.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
          </h1>
          <p className="mt-1 mb-0 text-[0.875rem] text-text-2">
            {consultation.practice_name ?? ''}
            {consultation.veterinarian_name ? ` · Dr. ${consultation.veterinarian_name}` : ''}
            {consultation.reason ? ` · ${consultation.reason}` : ''}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <span
            className="inline-flex items-center py-[0.3rem] px-3 rounded-full text-[0.78rem] font-bold"
            style={isCompleted
              ? { background: 'var(--accent-muted)', color: 'var(--accent)' }
              : { background: 'var(--warning-bg)', color: 'var(--warning-text)' }
            }
          >
            {isCompleted ? 'Completed' : 'Open'}
          </span>
          {consultation.prescriptions.length > 0 && (
            <Button
              variant="outline" size="sm"
              onClick={() => window.open(`/consultations/${consultation.id}/prescription`, '_blank')}
            >
              Print Prescription
            </Button>
          )}
          <ButtonLink
            to={`/invoices/new?consultation_id=${consultation.id}&patient_id=${consultation.patient_id}`}
            variant="outline"
            size="sm"
          >
            Create Invoice
          </ButtonLink>
          <Button
            variant="outline" size="sm"
            onClick={() => followUpClientId
              ? setShowFollowUp(true)
              : toast.error('This patient has no owner on file — cannot schedule a follow-up.')}
          >
            Schedule Follow-up
          </Button>
          {canEditClinical && (
            isCompleted
              ? <Button variant="outline" size="sm" onClick={() => void handleReopen()}>Reopen</Button>
              : <Button size="sm" onClick={() => void handleComplete()}>Mark Complete</Button>
          )}
        </div>
      </div>

      {isCompleted && (
        <div className="bg-warning-bg border border-warning-border rounded-md px-4 py-3 mb-5 text-[0.85rem] text-warning-text">
          This consultation is completed. Reopen it to make changes.
        </div>
      )}

      <div className="grid gap-5">
        <VaccinationPanel patientId={consultation.patient_id} />

        {(isCompleted || !canEditClinical) ? (
          // Read-only SOAP view
          <Card>
            <SectionTitle>SOAP Note</SectionTitle>
            {(['subjective', 'objective', 'assessment', 'plan'] as const).map(field => (
              <div key={field} className="mb-4">
                <span className={soapLabelClass}>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                <p className={`m-0 text-[0.9rem] whitespace-pre-wrap ${consultation.soap_note?.[field] ? 'text-text' : 'text-text-4 italic'}`}>
                  {consultation.soap_note?.[field] ?? 'Not recorded'}
                </p>
              </div>
            ))}
          </Card>
        ) : (
          <SoapSection consultationId={consultation.id} initial={consultation.soap_note} />
        )}

        {(isCompleted || !canEditClinical) ? (
          <Card>
            <SectionTitle>Vital Signs</SectionTitle>
            <div className="grid grid-cols-4 gap-4 max-sm:grid-cols-2">
              {[
                { label: 'Weight', value: consultation.vital_signs?.weight_kg != null ? `${consultation.vital_signs.weight_kg} kg` : '—' },
                { label: 'Temperature', value: consultation.vital_signs?.temperature_c != null ? `${consultation.vital_signs.temperature_c}°C` : '—' },
                { label: 'Heart Rate', value: consultation.vital_signs?.heart_rate != null ? `${consultation.vital_signs.heart_rate} bpm` : '—' },
                { label: 'Resp. Rate', value: consultation.vital_signs?.respiratory_rate != null ? `${consultation.vital_signs.respiratory_rate} br/min` : '—' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div className="text-[0.72rem] font-bold text-text-3 uppercase tracking-[0.04em] mb-1">{label}</div>
                  <div className={`text-[1rem] font-semibold ${value === '—' ? 'text-text-4' : 'text-text-heading'}`}>{value}</div>
                </div>
              ))}
            </div>
            {consultation.vital_signs?.notes && (
              <p className="mt-3 mb-0 text-[0.85rem] text-text-2">{consultation.vital_signs.notes}</p>
            )}
          </Card>
        ) : (
          <VitalsSection consultationId={consultation.id} initial={consultation.vital_signs} />
        )}

        {(isCompleted || !canEditClinical) ? (
          <Card>
            <SectionTitle>Diagnoses</SectionTitle>
            {consultation.diagnoses.length === 0
              ? <p className="m-0 text-[0.875rem] text-text-4 italic">None recorded</p>
              : (
                <ul className="m-0 pl-5">
                  {consultation.diagnoses.map(d => (
                    <li key={d.id} className="text-[0.9rem] text-text mb-[0.35rem]">
                      {d.is_primary && <strong>[Primary] </strong>}{d.description}
                    </li>
                  ))}
                </ul>
              )
            }
          </Card>
        ) : (
          <DiagnosesSection consultationId={consultation.id} initial={consultation.diagnoses} />
        )}

        {(isCompleted || !canEditClinical) ? (
          <Card>
            <SectionTitle>Prescriptions</SectionTitle>
            {consultation.prescriptions.length === 0
              ? <p className="m-0 text-[0.875rem] text-text-4 italic">None recorded</p>
              : consultation.prescriptions.map(p => (
                <div key={p.id} className="mb-3 pb-3 border-b border-border">
                  <strong className="text-[0.9rem]">{p.medication_name}</strong>
                  {(p.dosage || p.frequency || p.duration) && (
                    <span className="text-[0.82rem] text-text-2 ml-2">
                      {[p.dosage, p.frequency, p.duration].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {p.instructions && (
                    <div className="text-[0.8rem] text-text-3 mt-[0.15rem]">{p.instructions}</div>
                  )}
                </div>
              ))
            }
          </Card>
        ) : (
          <PrescriptionsSection
            consultationId={consultation.id}
            practiceId={consultation.practice_id}
            initial={consultation.prescriptions}
            onDispensed={() => setMovementsKey(k => k + 1)}
          />
        )}

        <DrugDispensingSection
          consultationId={consultation.id}
          practiceId={consultation.practice_id}
          patientId={consultation.patient_id}
          refreshKey={movementsKey}
          canDispense={canEditClinical}
        />
      </div>

      {showFollowUp && followUpClientId && (
        <AppointmentBookingModal
          title="Schedule Follow-up"
          initialDate={followUpDate()}
          initialTime={followUpTime()}
          initialDuration={30}
          lockedClientId={followUpClientId}
          lockedPatientId={consultation.patient_id}
          lockedPracticeId={consultation.practice_id}
          defaultAppointmentType="follow_up"
          onClose={() => setShowFollowUp(false)}
          onCreated={() => {
            setShowFollowUp(false)
            toast.success('Follow-up appointment scheduled.')
          }}
        />
      )}
    </div>
  )
}

// ── Drug Dispensing Section ───────────────────────────────────────────────────

function DrugDispensingSection({ consultationId, practiceId, patientId, refreshKey, canDispense }: {
  consultationId: number
  practiceId: number
  patientId: number
  refreshKey: number
  canDispense: boolean
}) {
  const [showForm, setShowForm] = useState(false)
  const { data: movements, loading, refetch } = useFetch<StockMovementRead[]>(
    () => listMovements({ practice_id: practiceId, consultation_id: consultationId }),
    [consultationId, practiceId, refreshKey],
  )

  const movementTypeLabel: Record<string, { label: string; bg: string; color: string }> = {
    receive:  { label: 'Receive',   bg: 'var(--success-bg)', color: 'var(--success-text)' },
    dispense: { label: 'Dispense',  bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
    adjust:   { label: 'Adjust',    bg: 'var(--surface-alt)', color: 'var(--text-2)' },
    write_off:{ label: 'Write-off', bg: 'var(--danger-bg)', color: 'var(--danger-text)' },
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Drug Dispensing</SectionTitle>
        {!showForm && canDispense && (
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>+ Dispense Drug</Button>
        )}
      </div>

      {showForm && (
        <div className="mb-5 p-4 bg-surface-alt rounded-lg">
          <DispenseForm
            practiceId={practiceId}
            consultationId={consultationId}
            patientId={patientId}
            onDispatched={() => { setShowForm(false); refetch() }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {loading && <p className="text-[0.875rem] text-text-3">Loading…</p>}
      {!loading && (!movements || movements.length === 0) && !showForm && (
        <p className="text-[0.875rem] text-text-3 m-0">No drugs dispensed in this consultation.</p>
      )}
      {movements && movements.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[0.85rem]">
            <thead>
              <tr>
                {['Date', 'Type', 'Product', 'Qty', 'Balance after'].map(h => (
                  <th key={h} className={thClass}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movements.map(m => {
                const badge = movementTypeLabel[m.movement_type] ?? { label: m.movement_type, bg: 'var(--surface-alt)', color: 'var(--text)' }
                return (
                  <tr key={m.id}>
                    <td className={tdClass}>
                      {new Date(m.movement_date).toLocaleDateString()}
                    </td>
                    <td className={tdClass}>
                      <span
                        className="rounded-full py-[0.15rem] px-[0.6rem] text-xs font-semibold"
                        style={{ background: badge.bg, color: badge.color }}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className={tdClass}>#{m.stock_batch_id}</td>
                    <td className={tdClass}>{m.quantity}</td>
                    <td className={tdClass}>{m.balance_after}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
